import { useState, useEffect } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, Input, Spinner,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { forwardInvoice, formatCurrency } from "./Api";

const LABEL_STYLE = {
  fontSize: "0.72rem", fontWeight: 700, color: "#334155",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
};

const TAG_INPUT_STYLE = {
  display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
  border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 10px",
  minHeight: 42, background: "#fff", cursor: "text",
};

const TagInput = ({ tags, onAdd, onRemove, placeholder, type = "text" }) => {
  const [inputVal, setInputVal] = useState("");

  const add = () => {
    const val = inputVal.trim();
    if (!val) return;
    if (type === "email") {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(val)) { toast.error("Invalid email address"); return; }
    }
    if (type === "tel") {
      const phoneRe = /^\+?[\d\s\-().]{7,}$/;
      if (!phoneRe.test(val)) { toast.error("Invalid phone number"); return; }
    }
    if (!tags.includes(val)) onAdd(val);
    setInputVal("");
  };

  return (
    <div style={TAG_INPUT_STYLE} onClick={(e) => e.currentTarget.querySelector("input")?.focus()}>
      {tags.map((tag, i) => (
        <span key={i} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE",
          borderRadius: 20, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600,
        }}>
          {tag}
          <button type="button" onClick={() => onRemove(i)} style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", color: "#6366F1",
          }}>
            <FeatherIcon icon="x" size={11} />
          </button>
        </span>
      ))}
      <input
        type={type}
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={tags.length === 0 ? placeholder : ""}
        style={{ border: "none", outline: "none", fontSize: "0.83rem", minWidth: 120, flexGrow: 1 }}
      />
    </div>
  );
};

const InvoiceForwardModal = ({ isOpen, onClose, invoice, onForwardSuccess }) => {
  const [emails, setEmails] = useState([]);
  const [phones, setPhones] = useState([]);
  const [recipientName, setRecipientName] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmails([]);
    setPhones([]);
    setRecipientName("");
    setSubject("");
    setMessage("");
    setIncludeAttachments(true);
  }, [isOpen]);

  const handleSend = async () => {
    if (emails.length === 0 && phones.length === 0) {
      toast.error("Add at least one email or phone number");
      return;
    }
    if (!invoice) return;
    setSending(true);
    try {
      const res = await forwardInvoice(invoice._id, {
        emails,
        phones,
        subject: subject || undefined,
        message: message || undefined,
        recipientName: recipientName || "Recipient",
        includeAttachments,
      });
      if (res?.success || res?.results?.some?.((r) => r.success)) {
        toast.success(res?.message || "Invoice forwarded");
        if (res?.communicationLog) onForwardSuccess?.(res.communicationLog);
        onClose();
      } else {
        toast.error(res?.message || "Failed to forward invoice");
      }
    } catch {
      // toast handled in api helper
    } finally {
      setSending(false);
    }
  };

  if (!invoice) return null;
  const attachmentCount = invoice.attachments?.length || 0;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="md">
      <ModalHeader
        toggle={onClose}
        style={{
          background: "linear-gradient(135deg,#0F172A 0%,#1E293B 100%)",
          borderRadius: "10px 10px 0 0", padding: "16px 24px",
          borderBottom: "2px solid #4F46E5",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "rgba(99,102,241,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FeatherIcon icon="send" size={16} color="#A5B4FC" />
          </div>
          <div>
            <div style={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>
              Forward Invoice
            </div>
            <div style={{ color: "#A5B4FC", fontSize: "0.75rem", marginTop: 2 }}>
              {invoice.title} · {formatCurrency(invoice.amount, invoice.currency)}
            </div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody style={{ padding: "20px 24px" }}>
        <FormGroup>
          <Label style={LABEL_STYLE}>Recipient Name</Label>
          <Input
            type="text"
            placeholder="e.g. Harry"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            style={{ borderRadius: 8, fontSize: "0.85rem", borderColor: "#E2E8F0" }}
          />
        </FormGroup>

        <FormGroup>
          <Label style={LABEL_STYLE}>
            <FeatherIcon icon="mail" size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />
            Emails
          </Label>
          <TagInput
            tags={emails}
            onAdd={(v) => setEmails((p) => [...p, v])}
            onRemove={(i) => setEmails((p) => p.filter((_, idx) => idx !== i))}
            placeholder="Type email + Enter"
            type="email"
          />
        </FormGroup>

        <FormGroup>
          <Label style={LABEL_STYLE}>
            <FeatherIcon icon="phone" size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />
            Phone Numbers (SMS)
          </Label>
          <TagInput
            tags={phones}
            onAdd={(v) => setPhones((p) => [...p, v])}
            onRemove={(i) => setPhones((p) => p.filter((_, idx) => idx !== i))}
            placeholder="Type phone + Enter"
            type="tel"
          />
        </FormGroup>

        <FormGroup>
          <Label style={LABEL_STYLE}>Subject (optional)</Label>
          <Input
            type="text"
            placeholder="Leave blank for auto-generated"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={{ borderRadius: 8, fontSize: "0.85rem", borderColor: "#E2E8F0" }}
          />
        </FormGroup>

        <FormGroup>
          <Label style={LABEL_STYLE}>Custom Message (optional)</Label>
          <Input
            type="textarea"
            rows={4}
            placeholder="Add a note for the recipient…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ borderRadius: 8, fontSize: "0.85rem", borderColor: "#E2E8F0" }}
          />
        </FormGroup>

        {attachmentCount > 0 && (
          <FormGroup check style={{ marginTop: 4 }}>
            <Input
              type="checkbox"
              checked={includeAttachments}
              onChange={(e) => setIncludeAttachments(e.target.checked)}
              id="iv-include-att"
            />
            <Label check for="iv-include-att" style={{ fontSize: "0.82rem", color: "#475569" }}>
              Attach {attachmentCount} file{attachmentCount !== 1 ? "s" : ""} to email
            </Label>
          </FormGroup>
        )}

        <div style={{
          background: "#EFF6FF", border: "1px solid #BFDBFE",
          borderRadius: 8, padding: "10px 14px", fontSize: "0.78rem",
          color: "#1E40AF", marginTop: 8, display: "flex", gap: 8, alignItems: "flex-start",
        }}>
          <FeatherIcon icon="info" size={14} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            A full audit log of every recipient + timestamp + sender will be saved on this invoice.
          </span>
        </div>
      </ModalBody>
      <ModalFooter style={{ borderTop: "1px solid #EEF2F7", padding: "12px 24px" }}>
        <button type="button" className="iv-btn iv-btn--ghost" onClick={onClose} disabled={sending}>
          Cancel
        </button>
        <button
          type="button"
          className="iv-btn iv-btn--primary"
          onClick={handleSend}
          disabled={sending || (emails.length === 0 && phones.length === 0)}
        >
          {sending ? <Spinner size="sm" /> : <FeatherIcon icon="send" size={13} />}
          Forward
        </button>
      </ModalFooter>
    </Modal>
  );
};

export { InvoiceForwardModal };
